/**
 * Swipe compare — spec §F5.10 ("swipe (vertical 4px brand handle, drag; thematic panes clip
 * right of handle; mutually exclusive with AOI clip — activating either pauses the other
 * with toast)") and §G6 ("Swipe sets `inset(0 0 0 {x}px)` on the same panes").
 *
 * Geometry, the `⇔` knob and the `ew-resize` cursor are the mockup's `#swipeHandle`. Two
 * things the mockup did not have:
 *
 * * the handle is a real `<button>` on an ARIA slider role and moves with ←/→ (and
 *   Home/End), because a pointer-only control fails §I5;
 * * the drag is pointer-capture based, so releasing outside the window still ends it.
 *
 * The x is deliberately **not** in the §F1 store: it is a transient pixel position in
 * container space, it changes every animation frame while dragging, and nothing outside
 * this component (permalink included) has any use for it. `swipe` — the on/off that §G6
 * makes exclusive with `aoiClip` — is the store's.
 *
 * **The handle's own x is container space; the pane's `inset()` must not be.** §G6 says
 * `inset(0 0 0 {x}px)` on the same two panes `AoiClip.ts` masks, and that carried the same
 * bug: `rasterPane`/`choroPane` sit under Leaflet's `.leaflet-map-pane`, which a pan moves by
 * rewriting its own `transform`, so `inset()` on the pane is resolved in the pane's **layer**
 * point space, not the container pixels the handle is drawn at. `x` (the button's `left` and
 * the drag math) rightly stays in container pixels — that DOM node is not inside the panned
 * pane — but the value written to `clip-path` is converted with
 * `map.containerPointToLayerPoint([x, 0]).x` first. That conversion is also why `move` stays
 * in the re-apply subscription below, unlike `AoiClip.ts`'s: the handle does not move on
 * screen during a pan, but the layer-space x that keeps it there does, so the inset would
 * silently drift back to a stale value the moment a pan started if `move` were dropped.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type L from 'leaflet';

import { useAppStore } from '../../lib/state';
import { setPaneClip } from './AoiClip';
import { useMap } from './mapContext';

/**
 * The pane UC2's land-cover comparison draws its **left** raster into (`panes.ts`), and
 * the complementary clip this module writes on it: `rasterPane` keeps everything right of
 * the handle, `swipeLeftPane` everything left of it, so the two rasters meet exactly at the
 * handle wherever the reader drags it.
 *
 * A polygon in layer points rather than a second `inset()`, for the reason `AoiClip.ts`
 * builds polygons: an `inset()`'s right-hand length is measured from the pane box's own
 * right edge, which is not a quantity this module knows, while a polygon's px coordinates
 * are measured from the pane's origin — the same origin the left inset above already uses.
 */
export const SWIPE_LEFT_PANE = 'swipeLeftPane';

/** Keeps the handle (and therefore a sliver of each side) reachable at any map width. */
export const SWIPE_EDGE_PX = 40;
/** Keyboard step for ←/→ — a comfortable nudge, not a pixel crawl. */
const KEY_STEP_PX = 24;

const clampX = (x: number, width: number): number =>
  Math.max(SWIPE_EDGE_PX, Math.min(width - SWIPE_EDGE_PX, x));

/**
 * Everything right of `layerX`, as a polygon in the pane's own layer points.
 *
 * NOTES #731: a Leaflet pane is a 0×0 positioned box, and `inset()` resolves against that
 * box, so `inset(0 0 0 x)` left nothing visible — the right-hand raster never showed. A
 * polygon with explicit coordinates does not depend on the box, which is why
 * `leftOfClip` (and `AoiClip.ts`) already use one.
 */
function rightOfClip(map: L.Map, layerX: number): string {
  const size = map.getSize();
  const topLeft = map.containerPointToLayerPoint([0, 0]);
  const bottomRight = map.containerPointToLayerPoint([size.x, size.y]);
  const pad = 2048;
  const x1 = bottomRight.x + pad;
  const y0 = topLeft.y - pad;
  const y1 = bottomRight.y + pad;
  return `polygon(${layerX}px ${y0}px, ${x1}px ${y0}px, ${x1}px ${y1}px, ${layerX}px ${y1}px)`;
}

/** Everything left of `layerX`, as a `clip-path` polygon in the pane's own layer points. */
function leftOfClip(map: L.Map, layerX: number): string {
  const size = map.getSize();
  const topLeft = map.containerPointToLayerPoint([0, 0]);
  const bottomRight = map.containerPointToLayerPoint([size.x, size.y]);
  // Generous overhang, so a tile drawn outside the current viewport (Leaflet keeps a ring
  // of them) is not clipped into a visible seam while the reader pans.
  const pad = 2048;
  const x0 = topLeft.x - pad;
  const y0 = topLeft.y - pad;
  const y1 = bottomRight.y + pad;
  return `polygon(${x0}px ${y0}px, ${layerX}px ${y0}px, ${layerX}px ${y1}px, ${x0}px ${y1}px)`;
}

/** Write (or release, with `''`) the left-hand pane's own clip. */
function setLeftPaneClip(map: L.Map, value: string): void {
  const pane = map.getPane(SWIPE_LEFT_PANE);
  if (pane) pane.style.clipPath = value;
}

export function Swipe() {
  const { t } = useTranslation();
  const map = useMap();
  const swipe = useAppStore((s) => s.swipe);
  const swipePair = useAppStore((s) => s.swipePair);
  const [x, setX] = useState<number | null>(null);
  // The applied x, read by the map-move handler without making it a render dependency.
  const xRef = useRef<number | null>(null);
  const draggingRef = useRef(false);

  /**
   * §G6: `inset(0 0 0 {x}px)` on `rasterPane` + `choroPane`, in **layer** points — see the
   * module header. `v` (container pixels) still drives the handle's own `left` and the ARIA
   * value; only the pane write goes through `containerPointToLayerPoint`.
   */
  // Read as a boolean rather than as the pair itself: `apply` only needs to know whether a
  // second pane is on screen, and the object identity changes on every store write.
  const pairOn = swipePair !== null;

  const apply = useCallback(
    (next: number) => {
      const width = map.getSize().x;
      const v = clampX(next, width);
      xRef.current = v;
      setX(v);
      const layerX = map.containerPointToLayerPoint([v, 0]).x;
      setPaneClip(map, rightOfClip(map, layerX));
      // The pair's left-hand raster is the mirror of that: everything *left* of the handle.
      setLeftPaneClip(map, pairOn ? leftOfClip(map, layerX) : '');
    },
    [map, pairOn],
  );

  useEffect(() => {
    if (!swipe) return;
    // Mid-map on activation (the mockup's `setSwipe(map.getSize().x / 2)`), or wherever the
    // reader last left the handle within this session.
    apply(xRef.current ?? map.getSize().x / 2);
    // A pan/zoom/resize does not move the handle on screen, but it moves the layer-space x
    // the inset is now written in (`containerPointToLayerPoint` above), so every one of these
    // has to re-run `apply` even though `v`/`xRef` are unchanged — re-clamped too, in case the
    // container just got narrower. `move` is the one that matters most here and the one
    // `AoiClip.ts` safely drops: mid-drag `apply` is called directly with the pointer's own
    // container x, but a *pan* changes the conversion without moving the handle, so without
    // `move` the inset would freeze at the pre-pan layer x while the handle stayed put on
    // screen — visibly detaching the clip edge from the handle. `viewreset` alongside the
    // other three because Leaflet fires it — and resets the pixel origin — on a
    // projection-origin reset, which `move`/`zoom` do not cover.
    const reapply = () => apply(xRef.current ?? map.getSize().x / 2);
    map.on('move zoom resize viewreset', reapply);
    return () => {
      map.off('move zoom resize viewreset', reapply);
      setPaneClip(map, '');
      setLeftPaneClip(map, '');
    };
  }, [map, swipe, apply]);

  const onPointerDown = (e: React.PointerEvent<HTMLButtonElement>) => {
    draggingRef.current = true;
    e.currentTarget.setPointerCapture(e.pointerId);
    e.preventDefault();
  };
  const onPointerMove = (e: React.PointerEvent<HTMLButtonElement>) => {
    if (!draggingRef.current) return;
    apply(e.clientX - map.getContainer().getBoundingClientRect().left);
  };
  const endDrag = (e: React.PointerEvent<HTMLButtonElement>) => {
    if (!draggingRef.current) return;
    draggingRef.current = false;
    if (e.currentTarget.hasPointerCapture(e.pointerId)) e.currentTarget.releasePointerCapture(e.pointerId);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    const width = map.getSize().x;
    const at = xRef.current ?? width / 2;
    if (e.key === 'ArrowLeft') apply(at - KEY_STEP_PX);
    else if (e.key === 'ArrowRight') apply(at + KEY_STEP_PX);
    else if (e.key === 'Home') apply(0);
    else if (e.key === 'End') apply(width);
    else return;
    e.preventDefault();
  };

  if (!swipe) return null;

  return (
    <button
      type="button"
      id="swipeHandle"
      role="slider"
      aria-label={t('tools.swipeHandle')}
      aria-orientation="vertical"
      aria-valuemin={0}
      aria-valuemax={Math.round(map.getSize().x)}
      aria-valuenow={Math.round(x ?? map.getSize().x / 2)}
      style={{ left: `${x ?? map.getSize().x / 2}px`, display: 'block' }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      onKeyDown={onKeyDown}
    />
  );
}

export default Swipe;
